import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  styled,
  Theme,
  CSSObject,
  useTheme,
  Box,
  createTheme,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import MailIcon from "@mui/icons-material/Mail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faBars,
  faXmark,
  faArrowRightFromBracket,
  faBullseye, 
  faChartSimple, 
  faCircleNodes, 
  faFile, 
  faLifeRing, 
  faList, 
  faTableColumns
} from "@fortawesome/free-solid-svg-icons";
import { ThemeProvider } from "@emotion/react";
import { relative } from "path";
import Tooltip from "@mui/material/Tooltip";
import { colors } from "../../common/pallette/colors";
import { useLocation, useNavigate } from "react-router-dom";
import { setInitialClub } from "../../redux/reducers/clubDetails";
import ChangeClubPopup from "../../containers/ChangeClubPopup";
import { setDrawer } from "../../redux/reducers/common";
import { AvatarAndName } from "../AvatarAndName";
import { baseUrl } from "../../api/config";
import { getUserAvatar } from "../../api/user";
import "./styles.css";

const theme = createTheme({
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          zIndex: 900,
          width: "auto",
          border: 0,
          // boxShadow: '3px 0px 5px #7D8793',
          backgroundColor: colors.black.dark,
          // marginLeft: -40,
          // transition: 'margin-left 0.5s',
          // '&:hover': {
          //     marginLeft: 0
          // }
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: "10px 14px",
          textAlign: "center",
          color: colors.white.bright,
          "&:hover": {
            backgroundColor: colors.white.light,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "auto",
          color: colors.white.bright,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          textAlign: "left",
          marginLeft: 20,
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white.bright,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 13,
        },
      },
    },
  },
});

const AppDrawer = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedClub = useSelector((state: any) => state.clubDetails);
  const isAdmin = useSelector((state: any) => state.common.isAdmin);
  const { isDrawerOpen, setDrawerIsOpen } = props;

  const data = [
    {title: 'Home', icon: faHouse, isAdminPage: true},
    {title: 'Evaluation', icon: faList, isAdminPage: false},
    {title: 'Licence', icon: faCircleNodes, isAdminPage: false},
    {title: 'Performance Plan', icon: faLifeRing, isAdminPage: false},
    {title: 'Goals & Strategy', icon: faBullseye, isAdminPage: false},
    {title: 'Evidence Library', icon: faFile, isAdminPage: false},
    {title: 'Dashboard', icon: faTableColumns, isAdminPage: true},
    {title: 'Licence Analytics', icon: faChartSimple, isAdminPage: false}
  ];

  const drawer = useSelector((state: any) => state.common.drawer);
  const [popUpisOpen, setPopUpIsOpen] = React.useState(false);
  const [avatar, setAvatar]:any = useState(null);
  const user = useSelector((state: any) => state.common.user);
  const [pendingNavigation, setPendingNavigation]:any = useState<{
    module: string;
    route: string;
  } | null>(null);

  // Module routes configuration
  const moduleRoutes: Record<string, string> = {
    "Evaluation": "/evaluation/criteria",
    "Licence": "/licence/criteria",
    "Evidence Library": "/evidencelibrary",
    "Performance Plan": "/performanceplan/projects",
  };

  // Effect to handle navigation after club selection
  useEffect(() => {
    if (pendingNavigation && selectedClub?.id !== 0 && !popUpisOpen) {
      dispatch(setDrawer(pendingNavigation.module));
      navigate(pendingNavigation.route);
      setPendingNavigation(null);
    }
  }, [selectedClub?.id, popUpisOpen, pendingNavigation]);

  const fetchModuleName = (path: any) => {
    if (path.includes("/home")) return "Home";
    if (path.includes("/evaluation/criteria") || path.includes("/onsite") || path.includes("/reports")) return "Evaluation";
    if (path.includes("/licence/criteria")) return "Licence";
    if (path.includes("/evidencelibrary")) return "Evidence Library";
    if (path.includes("/performanceplan")) return "Performance Plan";
    if (path.includes("/goals")) return "Goals & Strategy";
    if (path.includes("/dashboard") && !path.includes("/licenceanalytics")) return "Dashboard";
    if (path.includes("/licenceanalytics")) return "Licence Analytics";
    if (path.includes("/settings")) return "Settings";
  };

  const manageModuleSelect = (moduleName: any) => {
    const module: any = moduleName.includes("/")
      ? fetchModuleName(moduleName)
      : moduleName;
    const isPath = moduleName.includes("/");

    switch (module) {
      case "Home":
        dispatch(setInitialClub());
        dispatch(setDrawer(module));
        !isPath && navigate("/home");
        break;
      case "Evaluation":
      case "Licence":
      case "Evidence Library":
      case "Performance Plan": {
        const route = moduleRoutes[module];
        if (selectedClub?.id !== 0 || !isAdmin) {
          dispatch(setDrawer(module));
          !isPath && navigate(route);
        } else {
          setPopUpIsOpen(true);
          setPendingNavigation({ module, route });
        }
        break;
      }
      case "Goals & Strategy":
        dispatch(setDrawer(module));
        !isPath && navigate("/goals");
        break;
      case "Dashboard":
        dispatch(setDrawer(module));
        !isPath && navigate("/dashboard/evaluation");
        break;
      case "Licence Analytics":
        dispatch(setDrawer(module));
        !isPath && navigate("/licenceanalytics/finance/dashboard");
        break;
      case "Settings":
        dispatch(setDrawer(module));
        !isPath && navigate("/settings");
        break;
    }
  };

  const handleDrawerClick = (data: any) => {
    const moduleName: any = data;
    if (
      selectedClub?.name == "J League" &&
      data !== "Home" &&
      data !== "Goals & Strategy" &&
      data !== "Dashboard" &&
      data !== "Licence Analytics" &&
      data !== "Settings"
    ) {
      setPopUpIsOpen(true);
      dispatch(setDrawer(moduleName));
    } else {
      manageModuleSelect(moduleName);
    }
  };

  useEffect(() => {
    const module: any = isAdmin ? "Home" : "Evaluation";
    (drawer == null || drawer === "") && dispatch(setDrawer(module));

    user &&
      user?.id &&
      !avatar &&
      getUserAvatar({ userId: user?.id }).then((res: any) => {
        setAvatar(res);
      });

    manageModuleSelect(location.pathname);
  }, [isAdmin, user, location.pathname]);

  const manageLogout = () => {
    window.localStorage.removeItem("__jauth__");
    window.localStorage.removeItem("_jauth_");
    window.localStorage.removeItem("lang");
    navigate("/");
  };

  const handleNavigateClub = (selectedClub: any) => {
    setPopUpIsOpen(false);
    // After popup closes and club is selected, the useEffect will handle navigation
    // if there's a pendingNavigation
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <ChangeClubPopup
          isOpen={popUpisOpen}
          handleIsOpen={handleNavigateClub}
        />
        <MuiDrawer variant="permanent">
          <Box>
            <ListItemButton onClick={() => setDrawerIsOpen(!isDrawerOpen)}>
              {!isDrawerOpen ? (
                <FontAwesomeIcon className="iconSize" icon={faBars} />
              ) : (
                <FontAwesomeIcon 
                  className="iconSize"
                  style={{ marginLeft: "auto" }}
                  icon={faXmark}
                />
              )}
            </ListItemButton>
          </Box>
          <Divider />
          <List
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box>
              {data.map((d: any, i: any) => {
                if (isAdmin || (!isAdmin && !d?.isAdminPage)) {
                  return (
                    <Tooltip
                      key={i}
                      title={isDrawerOpen ? "" : d.title}
                      placement="right"
                      arrow
                    >
                      <ListItem onClick={() => manageModuleSelect(d.title)}>
                        <ListItemButton>
                          <ListItemIcon>
                            <FontAwesomeIcon
                              className="mlrAuto iconSize"
                              icon={d.icon}
                              color={drawer == d.title ? colors.blue.navy : ""}
                            />
                          </ListItemIcon>
                          {isDrawerOpen && <ListItemText>{d.title}</ListItemText>}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  );
                }
              })}
            </Box>
            <Box>
              <Divider />
              <Tooltip
                title={isDrawerOpen ? "" : user?.displayName}
                placement="right"
                arrow
              >
                <ListItem>
                  <ListItemButton
                    onClick={() => {
                      manageModuleSelect("Settings");
                    }}>
                    <ListItemIcon>
                      <AvatarAndName avatarImage={avatar ? avatar : ""} />
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className="iconSize">{user?.displayName}</ListItemText>}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
              {/* <Tooltip title={isDrawerOpen ? "" : "Settings"} placement="right" arrow>
                <ListItem>
                  <ListItemButton
                    onClick={() => {
                      manageModuleSelect("Settings");
                    }}
                  >
                    <ListItemIcon>
                      <FontAwesomeIcon className="mlrAuto" icon={faGear} />
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText>Settings</ListItemText>}
                  </ListItemButton>
                </ListItem>
              </Tooltip> */}
              <Tooltip title={isDrawerOpen ? "" : "Logout"} placement="right" arrow>
                <ListItem>
                  <ListItemButton onClick={manageLogout}>
                    <ListItemIcon>
                      <FontAwesomeIcon
                        className="mlrAuto"
                        icon={faArrowRightFromBracket}
                      />
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText>Logout</ListItemText>}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            </Box>
          </List>
        </MuiDrawer>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default AppDrawer;